import type { ApiClient } from '@commander/api-client';
import type { UpdateUserSettingsRequest } from './types';

/**
 * Updates user theme preference via API
 *
 * @param apiClient - The API client instance
 * @param appearance - Theme appearance setting (LIGHT_MODE | DARK_MODE)
 * @returns Promise that resolves when update is complete
 * @throws Error if API call fails
 */
export async function updateUserThemeSetting(
  apiClient: ApiClient,
  appearance: UpdateUserSettingsRequest['appearance']
): Promise<void> {
  try {
    const response = await apiClient.put<{
      success: boolean;
      message?: string;
    }>('/users/settings', {
      appearance,
    } as UpdateUserSettingsRequest);

    if (!response.success) {
      throw new Error(response.message || 'Failed to update theme setting');
    }
  } catch (error) {
    console.error('Failed to update user theme setting:', error);

    if (error instanceof Error) {
      throw new Error(`Theme update failed: ${error.message}`);
    }

    throw new Error('An unexpected error occurred while updating theme setting');
  }
}

/**
 * Validates theme mode string
 *
 * @param mode - Theme mode to validate
 * @returns True if valid theme mode
 */
export function isValidThemeMode(mode: string): mode is 'LIGHT_MODE' | 'DARK_MODE' | 'SYSTEM' {
  return ['LIGHT_MODE', 'DARK_MODE', 'SYSTEM'].includes(mode);
}

/**
 * Normalizes theme mode from various formats
 *
 * @param mode - Theme mode in any format
 * @returns Normalized theme mode
 */
export function normalizeThemeMode(
  mode: string | null | undefined
): 'LIGHT_MODE' | 'DARK_MODE' | 'SYSTEM' {
  if (!mode) return 'SYSTEM';

  const upperMode = mode.toUpperCase();

  // Handle various input formats
  if (upperMode === 'LIGHT' || upperMode === 'LIGHT_MODE') {
    return 'LIGHT_MODE';
  }

  if (upperMode === 'DARK' || upperMode === 'DARK_MODE') {
    return 'DARK_MODE';
  }

  if (upperMode === 'SYSTEM') {
    return 'SYSTEM';
  }

  // Default to system for unrecognized values
  return 'SYSTEM';
}
